import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline'
import React, { cloneElement, ReactElement, useRef } from 'react'
import FrameIcon from '../Icon/FrameIcon'
import { twMerge } from 'tailwind-merge'

type Props = {
  title?: string
  isExpanded?: boolean
  canNavigate?: boolean
  subheader?: ReactElement<{ isExpanded?: boolean }>
  onToggleExpand?: () => void
  onClose?: () => void
  onPrevious?: () => void
  onNext?: () => void
  banner?: ReactElement<{ isExpanded?: boolean }>
}

export default function DrawerHeaderV2({
  title,
  isExpanded = false,
  canNavigate = true,
  subheader,
  onToggleExpand,
  onClose,
  onPrevious,
  onNext,
  banner,
}: Props): ReactElement {
  const previousButtonRef = useRef<HTMLButtonElement>(null)
  const nextButtonRef = useRef<HTMLButtonElement>(null)

  return (
    <div className={`flex flex-col ${isExpanded ? '' : 'max-w-[420px]'}`}>
      <div
        className="flex w-full items-center justify-between px-1 py-2"
        onKeyDown={(e) => {
          if (e.key === `ArrowUp`) {
            onPrevious?.()
            previousButtonRef.current?.focus()
          }
          if (e.key === `ArrowDown`) {
            onNext?.()
            nextButtonRef.current?.focus()
          }
        }}
        aria-label="Drawer Header"
      >
        {/* Left side - Navigation and Title */}
        <div className="flex items-center gap-2">
          {canNavigate && (
            <div className="flex items-center gap-1">
              <button
                ref={previousButtonRef}
                onClick={onPrevious}
                className="flex h-5 w-5 items-center justify-center rounded-[6px] border border-default bg-transparent outline-none hover:bg-surface-muted focus-visible:ring-1 focus-visible:ring-blue-500"
                aria-label="Previous"
                tabIndex={0}
              >
                <ChevronDownIcon className="h-3 w-3 -scale-y-100 text-subtle" strokeWidth={2} />
              </button>
              <button
                ref={nextButtonRef}
                onClick={onNext}
                className="flex h-5 w-5 items-center justify-center rounded-[6px] border border-default bg-transparent outline-none hover:bg-surface-muted focus-visible:ring-1 focus-visible:ring-blue-500"
                aria-label="Next"
                tabIndex={0}
              >
                <ChevronDownIcon className="h-3 w-3 text-subtle" strokeWidth={2} />
              </button>
            </div>
          )}
          <span className={twMerge('text-[14px] font-medium leading-[1.4] text-subtle', !canNavigate && `pl-4`)}>
            {title}
          </span>
        </div>

        {/* Right side - Narrow and Close buttons */}
        <div className="flex items-center gap-1">
          {/* The expand/narrow toggle only makes sense on desktop's two-pane
              layout; the mobile drawer is a single tabbed column, so hide it. */}
          <button
            onClick={onToggleExpand}
            className="hidden h-5 w-5 items-center justify-center text-subtle hover:bg-surface-muted md:flex"
            aria-label="Toggle expand"
          >
            <FrameIcon className="h-full w-full text-subtle" variant={isExpanded ? `narrow` : `wide`} />
          </button>

          <button
            onClick={onClose}
            className="flex h-5 w-5 items-center justify-center hover:bg-surface-subtle"
            aria-label="Close"
          >
            <XMarkIcon className="h-full w-full text-subtle" />
          </button>
        </div>
      </div>
      {banner && cloneElement(banner, { isExpanded })}
      {subheader && (
        <div className="mx-1 mb-1 min-h-[72px] rounded-md bg-surface p-4">
          {cloneElement(subheader, { isExpanded })}
        </div>
      )}
    </div>
  )
}
